import type React from "react";
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

interface DoNotOutputModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string, notes?: string) => void;
  isLoading?: boolean;
}

const reasonOptions = [
  { value: "insufficient_documentation", label: "Insufficient documentation" },
  { value: "unclear_procedures", label: "Unclear or ambiguous procedures" },
  { value: "missing_diagnosis", label: "Missing information to verify diagnosis" },
  { value: "coding_complexity", label: "Case too complex for automated coding" },
  { value: "quality_concerns", label: "Documentation quality concerns" },
  { value: "other", label: "Other (please specify)" },
];

export const DoNotOutputModal: React.FC<DoNotOutputModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
}) => {
  const [selectedReason, setSelectedReason] = useState("");
  const [notes, setNotes] = useState("");

  const handleConfirm = () => {
    if (!selectedReason) return;
    onConfirm(selectedReason, notes.trim() || undefined);
  };

  const handleClose = () => {
    setSelectedReason("");
    setNotes("");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Do Not Output Codes</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-sm font-medium mb-3 block">Please select a reason for not outputting codes:</Label>
            <RadioGroup value={selectedReason} onValueChange={setSelectedReason}>
              {reasonOptions.map((option) => (
                <div key={option.value} className="flex items-center space-x-2">
                  <RadioGroupItem value={option.value} id={option.value} />
                  <Label htmlFor={option.value} className="text-sm cursor-pointer">
                    {option.label}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          {selectedReason === "other" && (
            <div>
              <Label htmlFor="notes" className="text-sm font-medium mb-2 block">
                Please specify:
              </Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Enter additional details..."
                rows={3}
              />
            </div>
          )}

          {selectedReason && selectedReason !== "other" && (
            <div>
              <Label htmlFor="notes" className="text-sm font-medium mb-2 block">
                Additional notes (optional):
              </Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Enter any additional notes..."
                rows={3}
              />
            </div>
          )}

          <div className="flex gap-2 pt-4">
            <Button variant="outline" onClick={handleClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button onClick={handleConfirm} disabled={!selectedReason || isLoading} className="flex-1">
              {isLoading ? "Processing..." : "Confirm - Do Not Output"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
